import { useState, useEffect, useCallback } from "react";
import { API, axios } from "@/api";
import { Loader2, TrendingUp } from "lucide-react";

export default function BrandTapAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/brand-tap-analytics`);
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-6">No analytics data yet</p>;

  return (
    <div data-testid="brand-tap-analytics-panel">
      {/* Summary stat */}
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-amber-500" />
        <p className="text-lg font-bold text-slate-800">{data.total_taps} total taps</p>
      </div>

      {/* Top brands */}
      {data.top_brands?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Top Tapped Brands</h4>
          <div className="space-y-1">
            {data.top_brands.slice(0, 10).map((b, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-slate-50 border border-slate-100">
                <span className="text-sm text-slate-700 font-medium">{b.brand || "Unknown"}</span>
                <span className="text-xs font-bold text-amber-600">{b.taps} taps</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top discoverers */}
      {data.top_discoverers?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Top Discoverers</h4>
          <div className="space-y-1">
            {data.top_discoverers.slice(0, 5).map((u, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-slate-50 border border-slate-100">
                <span className="text-xs text-slate-500 font-mono">{(u.user_id || "").slice(0, 12)}...</span>
                <span className="text-xs font-bold text-sky-600">{u.brand_count} brands</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily trend */}
      {data.daily_trend?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Daily Trend (14d)</h4>
          <div className="flex items-end gap-1 h-16">
            {data.daily_trend.map((d, i) => {
              const max = Math.max(...data.daily_trend.map(x => x.count), 1);
              const h = (d.count / max) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-amber-400 rounded-t" style={{ height: `${Math.max(h, 4)}%` }} title={`${d.date}: ${d.count}`} />
                  <span className="text-[7px] text-slate-400 mt-0.5">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
